import { useState } from "react";
import axios from "axios";
import { InfoIcon, SwapIcon } from "../../../assets/icons/flowbite-icons";
import {
  signEvmTransactionWithRetry,
  waitForEVMTransaction,
} from "../../../utils/wallets";
import { Message, ResponseType } from "../../../hooks/use-chat";
import { TransactionStatusMessage } from "./transaction-status";

interface SwapQuote {
  inToken: {
    address: string;
    symbol: string;
    decimals: string;
    icon: string;
  };
  inAmount: string;
  inAmountUSD: string;
  inTokenBalance: string;
  outToken: {
    address: string;
    symbol: string;
    decimals: string;
    icon: string;
  };
  outAmount: string;
  outAmountUSD: string;
  totalFeeAmountUSD: string;
}
interface Transaction {
  from: string;
  to: string;
  value: number;
  data: string;
  gasPrice: number;
  chainId: number;
}

interface SwapMessage {
  transactionId: string;
  transactions: Transaction[];
  quote: SwapQuote;
}
export const Swap = ({
  message,
  setMessages,
  accessToken,
  setError,
}: {
  message: string;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  accessToken: string | null;
  setError: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const [transactionSignature, setTransactionSignature] = useState<string>("");
  const [transactionChain, setTransactionChain] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { transactionId, transactions, quote }: SwapMessage =
    JSON.parse(message);
  async function submitTransactionStatus(status: string, signature: string) {
    try {
      if (!transactionId) return;
      const response = await axios.post(
        `${process.env.REACT_APP_CREATOR_ENDPOINT}/submit/transaction-status`,
        {
          status,
          signature,
          transactionId: transactionId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status !== 200) {
        console.error("Failed to submit transaction status");
      }
    } catch (e: any) {
      console.error("error in submitTransactionStatus:", e);
      setError(
        "There seems to be an error submitting the transaction. Please check your wallet and the Kinetix Finance Dashboard for any possible status updates, since your transaction may have possibly been successful but an error may have been received here. If you don't see any updates in your wallet or the Kinetix Dashboard, then please try again."
      );
    }
  }

  async function signTransaction() {
    try {
      if (!transactions) return;
      // first we need to approve allowance and then swap
      let lastTransactionHash = "";
      let lastTransactionChain = "";

      for (let i = 0; i < transactions.length; i++) {
        setLoading(true);
        const transactionHash = await signEvmTransactionWithRetry(
          transactions[i]
        );

        // wait for the transaction to be confirmed
        await waitForEVMTransaction(
          transactionHash.toString(),
          transactions[i].chainId
        );

        lastTransactionHash = transactionHash.toString();
        lastTransactionChain = transactions[i].chainId.toString();
      }

      setTransactionSignature(lastTransactionHash);
      setTransactionChain(lastTransactionChain);
      const transactionStatusMessage: TransactionStatusMessage = {
        status: "success",
        content: lastTransactionHash,
        chain: lastTransactionChain,
      };
      submitTransactionStatus("success", lastTransactionHash);
      setMessages((m: Message[]) => [
        ...m,
        {
          agent: "ui",
          message: "",
          component: ResponseType.TRANSACTION_STATUS,
          data: JSON.stringify(transactionStatusMessage),
        },
      ]);
    } catch (e: any) {
      console.error(e);
      const transactionStatusMessage: TransactionStatusMessage = {
        status: "error",
        content: e.response?.data?.error ?? e.message,
        chain: transactionChain,
      };
      submitTransactionStatus("failure", "");
      setMessages((m: Message[]) => [
        ...m,
        {
          agent: "ui",
          message: "",
          component: ResponseType.TRANSACTION_STATUS,
          data: JSON.stringify(transactionStatusMessage),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div
        className={`bg-gray-800 border  shadow-sm rounded-lg p-4 mt-4 flex flex-col items-center gap-4 min-w-full ${transactionSignature ? "border-green-900" : "border-gray-700"
          } ${loading ? "animate-pulse" : ""}`}
      >
        <div className="bg-gray-700 border border-gray-600 rounded-lg p-3 flex justify-between items-center gap-4 w-full h-70px">
          {/* Input Token */}
          <div className="flex flex-row items-start gap-2">
            <img
              src={quote?.inToken.icon}
              alt={quote?.inToken.symbol}
              className="w-6 h-6 rounded-full"
            />
            <div className="flex flex-col justify-center gap-1">
              <span className="text-white font-medium text-sm leading-6">
                {quote?.inAmount} {quote?.inToken.symbol}
              </span>
              <span className="text-gray-4000 text-sm leading-6">
                ${Number(quote?.inAmountUSD).toFixed(2)}
              </span>
            </div>
          </div>
          <div>
            <SwapIcon />
          </div>
          {/* Output Token */}
          <div className="flex flex-row items-start gap-2">
            <img
              src={quote?.outToken.icon}
              alt={quote?.outToken.symbol}
              className="w-6 h-6 rounded-full"
            />
            <div className="flex flex-col justify-center gap-1">
              <span className="text-white font-medium text-sm leading-6">
                {quote?.outAmount} {quote?.outToken.symbol}
              </span>
              <span className="text-gray-400 text-sm leading-6">
                ${Number(quote?.outAmountUSD).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="flex flex-row items-center px-1 w-full">
          <span className="text-white font-semibold text-sm leading-6">
            Balance
          </span>
          <span className="text-white text-sm leading-6 ml-auto">
            {Number(quote?.inTokenBalance).toFixed(6)} {quote?.inToken.symbol}
          </span>
        </div>
        {Number(quote?.totalFeeAmountUSD) > 0 && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              Fees
            </span>
            <span className="text-white text-sm leading-6 ml-auto">
              US${Number(quote?.totalFeeAmountUSD).toFixed(2)}
            </span>
          </div>
        )}
        <div className="flex flex-row items-center px-1 w-full">
          <InfoIcon />
          <span className="text-white font-semibold text-sm leading-6 ml-2">
            Total (USD)
          </span>
          <span className="text-white text-sm leading-6 ml-auto">
            $
            {(
              Number(quote?.inAmountUSD) + Number(quote?.totalFeeAmountUSD)
            ).toFixed(2)}
          </span>
        </div>
        {!transactionSignature && (
          <button
            className="bg-gradient-to-r from-[#EF533D] to-[#F39537] text-white font-semibold rounded-lg p-2 w-full"
            onClick={signTransaction}
            disabled={loading}
          >
            {!loading ? "Swap" : "Waiting for signature..."}
          </button>
        )}
      </div>
    </>
  );
};
